declare global {
  interface Window {
    __PRERENDER_INJECTED: boolean
  }
  interface ace {
    Editor: any
  }
}

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { FILE_MESSAGE_TYPE } from '@/utils/embeds/ide'

import {
  useEmbedConfigureStore,
  type IEmbedLanguage,
  type IInputFiles
} from '@/stores/embeds/embedConfigure.store'

export interface ILibsLanguage {
  [x: string]: string[]
}
type TFileMessage = {
  type: (typeof FILE_MESSAGE_TYPE)[keyof typeof FILE_MESSAGE_TYPE]
  message: string | null
}

export const useIdeStore = defineStore('embedIde', () => {
  const isPymKey = ref<string | null>(null)
  const pymChild = ref<any | null>(null)

  const shareNotFound = ref<boolean>(false)
  const shareNotFoundHttpError = ref<string | null>(null)

  const codeEditorModal = ref<any | null>(null)
  const codeEditorRef = ref<HTMLElement | null>(null)
  const codeEditor = ref<ace['Editor'] | null>(null)
  const onEditThemeView = ref<boolean>(false)
  const onEditView = ref<boolean>(false)

  const outputEditorModal = ref<any | null>(null)
  const outputEditorRef = ref<HTMLElement | null>(null)
  const outputEditor = ref<ace['Editor'] | null>(null)

  const selectedLanguage = ref<IEmbedLanguage | null>(null)
  const languageListHideTitle = ref<boolean>(true)
  const languageListTitleName = ref<string>('Select Language')
  const languageListDisplay = ref<boolean>(true)

  const selectedLanguageVersionsList = ref<string[]>([])
  const selectedVersion = ref<number | null>(null)
  const versionListHideTitle = ref<boolean>(true)
  const versionListTitleName = ref<string>('Select Version')
  const versionListDisplay = ref<boolean>(true)

  const stdinArgs = ref<string>('')
  const stdinArgsHideTitle = ref<boolean>(true)
  const stdinArgsTitleName = ref<string>('STDIN Inputs')
  const stdinArgsDisplay = ref<boolean>(true)
  const stdinArgsLines = ref<number>(1)

  const inputArgs = ref<string>('')
  const inputArgsHideTitle = ref<boolean>(true)
  const inputArgsTitleName = ref<string>('INPUT Inputs')
  const inputArgsDisplay = ref<boolean>(true)
  const inputArgsLines = ref<number>(1)

  const interActiveMode = ref<boolean>(false)
  const interactiveToggleHideTitle = ref<boolean>(true)
  const interactiveToggleTitleName = ref<string>('Interactive Mode:')
  const interactiveToggleDisplay = ref<boolean>(true)

  const libsLanguage = ref<ILibsLanguage[]>([])
  const libscanEdit = ref<boolean>(true)

  const uploadInProgress = ref<boolean>(false)
  const maxUploads = ref<number>(5)
  const maxFileSize = ref<number>(5000000)
  const fileUploadMessages = ref<TFileMessage | null>(null)
  const inputFiles = ref<IInputFiles[]>([])
  const inputFilesCanUpload = ref<boolean>(true)

  const outputFiles = ref<string[]>([])

  const executionTime = ref<string | null>(null)
  const memory = ref<number | string | null>(null)
  const cpuTime = ref<number | string | null>(null)
  const exeMatrixHideTitle = ref<boolean>(true)
  const exeMatrixTitleName = ref<string>('Execution Matrices:')
  const exeMatrixDisplay = ref<boolean>(true)

  const fontSize = ref<number | null>(null)
  const fontSizeHideTitle = ref<boolean>(true)
  const fontSizeTitleName = ref<string>('Editor Font Size:')
  const fontSizeDisplay = ref<boolean>(true)

  const isInputFilesUpdated = ref<boolean>(false)
  const isCodeExecuting = ref<boolean>(false)

  const socketClient = ref<any | null>(null)
  const wsNextId = ref<number>(0)
  const socketConnected = ref<boolean>(false)

  const getCodeEditor = ref<ace['Editor'] | null>(null)
  const getCodeScript = ref<string>('')

  const featurePermissionError = ref<string | null>(null)
  const onExecuteCompleteEnabled = ref<boolean>(false)

  const getLanguageListHideTitle = computed(() => {
    return languageListHideTitle.value
  })
  const getLanguageListTitleName = computed(() => {
    return languageListTitleName.value
  })
  const getLanguageListDisplay = computed(() => {
    return languageListDisplay.value
  })

  const getVersionListHideTitle = computed(() => {
    return versionListHideTitle.value
  })
  const getVersionListTitleName = computed(() => {
    return versionListTitleName.value
  })
  const getVersionListDisplay = computed(() => {
    return versionListDisplay.value
  })

  const getStdinArgsHideTitle = computed(() => {
    return stdinArgsHideTitle.value
  })
  const getStdinArgsTitleName = computed(() => {
    return stdinArgsTitleName.value
  })
  const getStdinArgsDisplay = computed(() => {
    return stdinArgsDisplay.value
  })
  const getStdinArgsLines = computed(() => {
    return stdinArgsLines.value
  })

  const getInputArgsHideTitle = computed(() => {
    return inputArgsHideTitle.value
  })
  const getInputArgsTitleName = computed(() => {
    return inputArgsTitleName.value
  })
  const getInputArgsDisplay = computed(() => {
    return inputArgsDisplay.value
  })
  const getInputArgsLines = computed(() => {
    return inputArgsLines.value
  })

  const getInteractiveToggleHideTitle = computed(() => {
    return interactiveToggleHideTitle.value
  })
  const getInteractiveToggleTitleName = computed(() => {
    return interactiveToggleTitleName.value
  })
  const getInteractiveToggleDisplay = computed(() => {
    return interactiveToggleDisplay.value
  })

  const getLibsCanEdit = computed(() => {
    return libscanEdit.value
  })

  const isUploadInProgress = computed(() => {
    return uploadInProgress.value
  })
  const isMaxUploads = computed(() => {
    return maxUploads.value
  })
  const isMaxFileSize = computed(() => {
    return maxFileSize.value
  })
  const isFileUploadMessages = computed(() => {
    return fileUploadMessages.value
  })
  const getInputFilesCanUpload = computed(() => {
    return inputFilesCanUpload.value
  })

  const getExeMatrixHideTitle = computed(() => {
    return exeMatrixHideTitle.value
  })
  const getExeMatrixTitleName = computed(() => {
    return exeMatrixTitleName.value
  })
  const getExeMatrixDisplay = computed(() => {
    return exeMatrixDisplay.value
  })

  const getFontSizeHideTitle = computed(() => {
    return fontSizeHideTitle.value
  })
  const getFontSizeTitleName = computed(() => {
    return fontSizeTitleName.value
  })
  const getFontSizeDisplay = computed(() => {
    return fontSizeDisplay.value
  })

  const isWsNextId = computed(() => {
    return wsNextId.value
  })
  const isSocketConnected = computed(() => {
    return socketConnected.value
  })

  /**
   * Check if ace editor is injected in global window object
   * @returns window.ace editor or null
   */
  const isWindowAce = () => {
    return window['ace'] || null
  }

  /**
   * pym key
   * @param value - The value to set
   */
  const setPymKey = (value: string | null) => {
    isPymKey.value = value
  }
  /**
   * Set the pym child
   * @param value - The value to set
   */
  const setPymChild = (value: any | null) => {
    pymChild.value = value
  }
  /**
   * Set the blockly editor languages
   * @param value - The value to set
   */
  const setShareNotFound = (value: boolean) => {
    shareNotFound.value = value
  }
  /**
   * Set shareNotFoundHttpError value
   * @param value - The value to set
   */
  const setShareNotFoundHttpError = (value: string | null) => {
    shareNotFoundHttpError.value = value
  }
  /**
   * Set the view mode to edit theme
   * @param value - The value to set
   */
  const setOnEditThemeView = (value: boolean) => {
    onEditThemeView.value = value
  }
  /**
   * Set the view mode to edit
   * @param value - The value to set
   */
  const setOnEditView = (value: boolean) => {
    onEditView.value = value
  }
  /**
   * Set the selected language
   * @param value - The value to set
   */
  const setSelectedLanguage = (value: IEmbedLanguage | null) => {
    selectedLanguage.value = value
  }
  /**
   * Set language list show title or not
   * @param value - The value to set
   */
  const setLanguageListHideTitle = (value: boolean) => {
    languageListHideTitle.value = value
  }
  /**
   * Set language list title name
   * @param value - The value to set
   */
  const setLanguageListTitleName = (value: string) => {
    languageListTitleName.value = value
  }
  /**
   * Set language list display or not
   * @param value - The value to set
   */
  const setLanguageListDisplay = (value: boolean) => {
    languageListDisplay.value = value
  }
  /**
   * Set the selected language versions list
   * @param value - The value to set
   */
  const setSelectedLanguageVersionsList = (value: string[]) => {
    selectedLanguageVersionsList.value = value
  }
  /**
   * Set the selected version
   * @param value - The value to set
   */
  const setSelectedVersion = (value: number | null) => {
    selectedVersion.value = value
  }
  /**
   * Set version list show title or not
   * @param value - The value to set
   */
  const setVersionListHideTitle = (value: boolean) => {
    versionListHideTitle.value = value
  }
  /**
   * Set version list title name
   * @param value - The value to set
   */
  const setVersionListTitleName = (value: string) => {
    versionListTitleName.value = value
  }
  /**
   * Set version list display or not
   * @param value - The value to set
   */
  const setVersionListDisplay = (value: boolean) => {
    versionListDisplay.value = value
  }
  /**
   * Set the stdin args
   * @param value - The value to set
   */
  const setStdinArgs = (value: string) => {
    stdinArgs.value = value
  }
  /**
   * Set stdin args show title or not
   * @param value - The value to set
   */
  const setStdinArgsHideTitle = (value: boolean) => {
    stdinArgsHideTitle.value = value
  }
  /**
   * Set stdin args title name
   * @param value - The value to set
   */
  const setStdinArgsTitleName = (value: string) => {
    stdinArgsTitleName.value = value
  }
  /**
   * Set stdin args display or not
   * @param value - The value to set
   */
  const setStdinArgsDisplay = (value: boolean) => {
    stdinArgsDisplay.value = value
  }
  /**
   * Set stdin args lines
   * @param value - The value to set
   */
  const setStdinArgsLines = (value: number) => {
    stdinArgsLines.value = value
  }
  /**
   * Set the input args
   * @param value - The value to set
   */
  const setInputArgs = (value: string) => {
    inputArgs.value = value
  }
  /**
   * Set input args show title or not
   * @param value - The value to set
   */
  const setInputArgsHideTitle = (value: boolean) => {
    inputArgsHideTitle.value = value
  }
  /**
   * Set input args title name
   * @param value - The value to set
   */
  const setInputArgsTitleName = (value: string) => {
    inputArgsTitleName.value = value
  }
  /**
   * Set input args display or not
   * @param value - The value to set
   */
  const setInputArgsDisplay = (value: boolean) => {
    inputArgsDisplay.value = value
  }
  /**
   * Set input args lines
   * @param value - The value to set
   */
  const setInputArgsLines = (value: number) => {
    inputArgsLines.value = value
  }
  /**
   * Set the interactive mode
   * @param value - The value to set
   */
  const setInteractiveMode = (value: boolean) => {
    interActiveMode.value = value
  }
  /**
   * Set interactive toggle show title or not
   * @param value - The value to set
   */
  const setInteractiveToggleHideTitle = (value: boolean) => {
    interactiveToggleHideTitle.value = value
  }
  /**
   * Set interactive toggle title name
   * @param value - The value to set
   */
  const setInteractiveToggleTitleName = (value: string) => {
    interactiveToggleTitleName.value = value
  }
  /**
   * Set interactive toggle display or not
   * @param value - The value to set
   */
  const setInteractiveToggleDisplay = (value: boolean) => {
    interactiveToggleDisplay.value = value
  }
  /**
   * Set the libs
   * @param value - The value to set
   */
  const setLibsLanguage = (value: ILibsLanguage[]) => {
    libsLanguage.value = value
  }
  /**
   * Set libs can edit or not
   * @param value - The value to set
   */
  const setLibsCanEdit = (value: boolean) => {
    libscanEdit.value = value
  }

  /**
   * Set upload in progress
   * @param value - The value to set
   */
  const setUploadInProgress = (value: boolean) => {
    uploadInProgress.value = value
  }

  /**
   * Set file error messages
   * @param type - The value to set
   * @param value - The value to set
   */
  const setFileUploadMessages = async (
    type: (typeof FILE_MESSAGE_TYPE)[keyof typeof FILE_MESSAGE_TYPE],
    value: string | null
  ) => {
    fileUploadMessages.value = {
      type: type,
      message: value
    }
    await new Promise((resolve) => setTimeout(resolve, 5000))
    fileUploadMessages.value = null
  }
  /**
   * Set input files
   * @param value - The value to set
   */
  const setInputFiles = (value: IInputFiles[]) => {
    inputFiles.value = value
  }
  /**
   * Set input files can upload or not
   * @param value - The value to set
   */
  const setInputFilesCanUpload = (value: boolean) => {
    inputFilesCanUpload.value = value
  }

  /**
   * Set output files
   * @param value - The value to set
   */
  const setOutputFiles = (value: string[]) => {
    outputFiles.value = value
  }

  /**
   * reset the execution data
   */
  const resetExecutionMatrix = () => {
    executionTime.value = null
    memory.value = null
    cpuTime.value = null
  }

  /**
   * Set the execution data
   * @param data - The data to set
   */
  const setExcecutionData = (data: any | null) => {
    executionTime.value = data
      ? data.executeTime
      : useEmbedConfigureStore().getOnEditMode
      ? 'N/A'
      : null
    memory.value = data ? data.memory : useEmbedConfigureStore().getOnEditMode ? 'N/A' : null
    cpuTime.value = data ? data.cpuTime : useEmbedConfigureStore().getOnEditMode ? 'N/A' : null
    outputFiles.value = data ? (data.outputFiles ? data.outputFiles : []) : []
  }
  /**
   * Set the execution time
   * @param value - The value to set
   */
  const setExecutionqTime = (value: string) => {
    executionTime.value = value
  }
  /**
   * Set exe matrix hide title or not
   * @param value - The value to set
   */
  const setExeMatrixHideTitle = (value: boolean) => {
    exeMatrixHideTitle.value = value
  }
  /**
   * Set exe matrix title name
   * @param value - The value to set
   */
  const setExeMatrixTitleName = (value: string) => {
    exeMatrixTitleName.value = value
  }
  /**
   * Set exe matrix display or not
   * @param value - The value to set
   */
  const setExeMatrixDisplay = (value: boolean) => {
    exeMatrixDisplay.value = value
  }

  /**
   * Set font size
   * @param value - The value to set
   */
  const setFontSize = (value: number | null) => {
    fontSize.value = value
  }
  /**
   * Set font size hide title or not
   * @param value - The value to set
   */
  const setFontSizeHideTitle = (value: boolean) => {
    fontSizeHideTitle.value = value
  }
  /**
   * Set font size title name
   * @param value - The value to set
   */
  const setFontSizeTitleName = (value: string) => {
    fontSizeTitleName.value = value
  }
  /**
   * Set font size display or not
   * @param value - The value to set
   */
  const setFontSizeDisplay = (value: boolean) => {
    fontSizeDisplay.value = value
  }
  /**
   * Set the input files updated
   * @param value - The value to set
   */
  const setInputFilesUpdated = (value: boolean) => {
    isInputFilesUpdated.value = value
  }
  /**
   * Set the code executing
   * @param value - The value to set
   */
  const setIsCodeExecuting = (value: boolean) => {
    isCodeExecuting.value = value
  }
  /**
   * Set socket next id
   * @param value - The value to set
   */
  const setwsNextId = (value: number) => {
    wsNextId.value = value
  }
  /**
   * Set socket connected
   * @param value - The value to set
   */
  const setSocketConnected = (value: boolean) => {
    socketConnected.value = value
  }
  /**
   * Set the code executing
   * @param value - The value to set
   */
  const setGetCodeScript = (value: string) => {
    getCodeScript.value = value
  }

  /**
   * set featurePermissionError
   * @param value - The value to set.
   */
  const setFeaturePermissionError = (value: string | null) => {
    featurePermissionError.value = value
  }
  /**
   * set onExecuteCompleteEnabled
   * @param value - The value to set.
   */
  const setOnExecuteCompleteEnabled = (value: boolean) => {
    onExecuteCompleteEnabled.value = value
  }

  return {
    isWindowAce,
    isPymKey,
    setPymKey,
    pymChild,
    setPymChild,
    shareNotFound,
    setShareNotFound,
    shareNotFoundHttpError,
    setShareNotFoundHttpError,
    codeEditorModal,
    codeEditorRef,
    codeEditor,
    onEditThemeView,
    setOnEditThemeView,
    onEditView,
    setOnEditView,
    outputEditorModal,
    outputEditorRef,
    outputEditor,
    selectedLanguage,
    setSelectedLanguage,
    getLanguageListHideTitle,
    setLanguageListHideTitle,
    getLanguageListTitleName,
    setLanguageListTitleName,
    getLanguageListDisplay,
    setLanguageListDisplay,
    selectedLanguageVersionsList,
    setSelectedLanguageVersionsList,
    selectedVersion,
    setSelectedVersion,
    getVersionListHideTitle,
    setVersionListHideTitle,
    getVersionListTitleName,
    setVersionListTitleName,
    getVersionListDisplay,
    setVersionListDisplay,
    stdinArgs,
    setStdinArgs,
    getStdinArgsHideTitle,
    setStdinArgsHideTitle,
    getStdinArgsTitleName,
    setStdinArgsTitleName,
    getStdinArgsDisplay,
    setStdinArgsDisplay,
    getStdinArgsLines,
    setStdinArgsLines,
    inputArgs,
    setInputArgs,
    getInputArgsHideTitle,
    setInputArgsHideTitle,
    getInputArgsTitleName,
    setInputArgsTitleName,
    getInputArgsDisplay,
    setInputArgsDisplay,
    getInputArgsLines,
    setInputArgsLines,
    interActiveMode,
    setInteractiveMode,
    getInteractiveToggleHideTitle,
    setInteractiveToggleHideTitle,
    getInteractiveToggleTitleName,
    setInteractiveToggleTitleName,
    getInteractiveToggleDisplay,
    setInteractiveToggleDisplay,
    libsLanguage,
    setLibsLanguage,
    getLibsCanEdit,
    setLibsCanEdit,
    isUploadInProgress,
    setUploadInProgress,
    isMaxUploads,
    isMaxFileSize,
    isFileUploadMessages,
    setFileUploadMessages,
    inputFiles,
    setInputFiles,
    getInputFilesCanUpload,
    setInputFilesCanUpload,
    outputFiles,
    setOutputFiles,
    executionTime,
    memory,
    cpuTime,
    getExeMatrixHideTitle,
    setExeMatrixHideTitle,
    getExeMatrixTitleName,
    setExeMatrixTitleName,
    getExeMatrixDisplay,
    setExeMatrixDisplay,
    resetExecutionMatrix,
    setExcecutionData,
    setExecutionqTime,
    fontSize,
    setFontSize,
    getFontSizeHideTitle,
    setFontSizeHideTitle,
    getFontSizeTitleName,
    setFontSizeTitleName,
    getFontSizeDisplay,
    setFontSizeDisplay,
    isCodeExecuting,
    isInputFilesUpdated,
    setInputFilesUpdated,
    setIsCodeExecuting,
    socketClient,
    wsNextId,
    isWsNextId,
    setwsNextId,
    isSocketConnected,
    setSocketConnected,
    getCodeScript,
    setGetCodeScript,
    getCodeEditor,
    featurePermissionError,
    setFeaturePermissionError,
    onExecuteCompleteEnabled,
    setOnExecuteCompleteEnabled
  }
})
